import { engineComponent, StringUtils, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ImageTextElement } from "../Components/Elements/Texts/ImageTextElement";
import { BetManager } from "../Managers/BetManager";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Languages } from "../Utils/Languages";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageTextElementAdaptive extends ImageTextCounterElement {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    private maxWidth: number;

    private maxMultiplier: number;

    private currentScale: number;

    //******************************************************************************************************************
    //  CONSTRUCTOR
    //******************************************************************************************************************

    constructor() {
        super();

        this.maxWidth = -1;
        this.maxMultiplier = -1;
        this.currentScale = -1;
    }

    //******************************************************************************************************************
    //  CREATE, UPDATE
    //******************************************************************************************************************

    public createByNode(node: Element) {
        super.createByNode(node);

        if (node.hasAttribute(ImageTextElementAdaptiveAttributes.MAX_WIDTH)) {
            this.maxWidth = parseInt(node.getAttribute(ImageTextElementAdaptiveAttributes.MAX_WIDTH));
        }

        if (node.hasAttribute(ImageTextElementAdaptiveAttributes.MAX_MULTIPLIER)) {
            this.maxMultiplier = parseInt(node.getAttribute(ImageTextElementAdaptiveAttributes.MAX_MULTIPLIER));
        }
    }

    //******************************************************************************************************************
    //  REALIGN
    //******************************************************************************************************************

    public realign(): void {
        //use always calculated scale based on max multiplier and max bet in case useMaxMultiplier is true
        if (this.maxMultiplier > -1 && this.scale.x != this.currentScale) {
            if (this.currentScale == -1) {
                this.currentScale = this.getScaleByMaxMultiplier();
            }
            this.bitmapText.scale.set(this.currentScale);
        } else if (this.maxWidth > -1) {
            //else check only max width attribute, if set, calculate adaptive scale for current value
            this.bitmapText.scale.set(1); //reset scale to get real width

            if (this.bitmapText.width > 0) {
                this.bitmapText.scale.set(this.getAdaptiveScale());
            }
        }

        super.realign();
    }

    protected getScaleByMaxMultiplier(): number {
        if (BetManager.TotalBets == null) {
            return -1;
        }

        const maxBet = Math.max.apply(null, BetManager.TotalBets);
        const maxSizedValue = Languages.formatText(
            Languages.FormatMoney,
            NumberUtils.multiply(maxBet, this.maxMultiplier),
        );
        const originText = this.bitmapText.text;
        let scale = 1;

        this.bitmapText.scale.set(1);
        this.bitmapText.text = maxSizedValue;

        if (this.maxWidth != null) {
            scale = this.getAdaptiveScale();
            this.bitmapText.text = originText;
        }

        return scale;
    }

    protected getAdaptiveScale(): number {
        const scale = this.maxWidth / this.bitmapText.width;
        return scale < 1 ? scale : 1;
    }

    //******************************************************************************************************************
    //  CLONE
    //******************************************************************************************************************

    public clone(): ImageTextElementAdaptive {
        let element: ImageTextElementAdaptive = new ImageTextElementAdaptive();
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;

        element.font = this.font;
        element.text = this.text;
        element.textAlign = this.textAlign;
        element.textBaseline = this.textBaseline;

        element.maxWidth = this.maxWidth;
        element.currentScale = this.currentScale;
        element.maxMultiplier = this.maxMultiplier;

        element.bitmapText = new PIXI.extras.BitmapText(this.text, { font: this.font });
        element.addChild(element.bitmapText);

        element.realign();
        element.markAsCloned();

        return element;
    }
}

export const ImageTextElementAdaptiveAttributes = {
    /**
     * Max possible multiplier in game used in values over symbols.
     * @property MaxWidth
     * @default -1
     */
    MAX_WIDTH: "MaxWidth",
    /**
     * Max possible multiplier in game used in values over symbols.
     * @property MaxMultiplier
     * @default -1
     */
    MAX_MULTIPLIER: "MaxMultiplier",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageTextElementAdaptive.ts
 **/